/** The link key: 32 random bytes, made in the browser and carried only in the link fragment. */
import { fromBase64Url, toBase64Url } from './base64url';
import { KEY_BYTES } from './labels';
import { DecryptError, importHkdfKey } from './primitives';
import { randomBytes } from './random';

const MALFORMED = 'The link is malformed or the encryption key is missing.';

/**
 * A fresh link key. `encoded` is what goes in the link fragment; `key` is the same bytes as a key that cannot be exported
 * again and can only derive the keys that seal (docs/FORMAT.md §1).
 */
export async function generateBridgeKey(): Promise<{ key: CryptoKey; encoded: string }> {
  const raw = randomBytes(KEY_BYTES);
  const encoded = toBase64Url(raw);
  try {
    return { key: await importHkdfKey(raw), encoded };
  } finally {
    raw.fill(0);
  }
}

export async function importBridgeKey(encoded: string): Promise<CryptoKey> {
  let raw: Uint8Array<ArrayBuffer>;
  try {
    raw = fromBase64Url(encoded);
  } catch {
    throw new DecryptError(MALFORMED);
  }
  // canonical text only: one link key has one spelling
  if (raw.length !== KEY_BYTES || toBase64Url(raw) !== encoded) throw new DecryptError(MALFORMED);
  try {
    return await importHkdfKey(raw);
  } finally {
    raw.fill(0);
  }
}
